// The compare-against filter chip-bar, ported to React from the legacy
// window.StatsFilterKit DOM UI (renderers-stats.js: renderFilterBar / renderAddMenu /
// openValuePicker / openRangePicker). This RETIRES the hybrid (04g-1's deferred follow-up):
// Rankings, Filter, and Stats all render this component instead of calling the legacy kit
// imperatively. The tuned PURE engine (matchesFilters / options / defaults / formatting)
// still lives in src/engines/stats-filter-kit.ts — only the DOM UI moved here.
//
// Contract with the consumer (unchanged from the imperative island): `active` is the
// module's own mutable `_*Filters` object; this component mutates it IN PLACE (add/edit/
// remove a chip, drag a range) and calls `onChange`, which the consumer turns into a
// version bump → a fresh clone into its results hook. Renders the chips + the "+ Add filter"
// wrap; the consumer composes the rest of `.filter-bar` around it (Rankings' search before,
// Filter's Reset-all after).
//
// Its pickers take part in the page-wide "stats" slot (picker-slot.ts), as the legacy's
// singletons: opening one closes the chip-bar picker open anywhere else.

import { useEffect, useRef, useState, type ReactNode } from "react";
import { ValuePicker, RangePicker, popoverPosUnder, type PopoverPos, type OptionInfo } from "./FilterPopovers.js";
import { closeAllPickers, useSlotPicker } from "./picker-slot.js";
import {
  isRangeFilter,
  formatRangeReadout,
  formatCategoricalValue,
  categoricalValueOf,
  categoricalFromValues,
  selectedValuesOf,
  isExcludeValue,
  excludeValue,
  playerFilterDefault,
  FILTER_LABELS,
  type ActiveFilters,
  type KitPlayer,
  type RangeActive,
  type CategoricalActive,
  type RangeFilterConfig,
} from "../engines/stats-filter-kit.js";
import { LIVE } from "../live/flag.js";

interface Props {
  active: ActiveFilters;
  /** Reference player — seeds a freshly-added categorical chip's default value. */
  refPlayer: KitPlayer;
  /** Distinct categorical values per filter (useFilterOptions). */
  options: Record<string, string[]>;
  rangeFilters: RangeFilterConfig;
  onChange: () => void;
  // Optional — only the live Stats tile sets these (T2-12b); every other chip bar is unchanged.
  /** The "+ Add filter" menu's options, in order (default: the snapshot's eight). */
  addMenuKeys?: readonly string[];
  /** Labels for chips the kit doesn't name (e.g. "Season"). */
  labels?: Record<string, string>;
  /** Chips shown quieter — Ben's untouched default chips (rules.md "Default chips are few and quiet"). */
  quietKeys?: ReadonlySet<string>;
  /** Per chip key: each value's heading + icon in its list (live League chip: country + flag). */
  optionInfo?: Record<string, (value: string) => OptionInfo | undefined>;
  /** A new chip's starting value, where the kit's default doesn't fit (Season: her season;
   *  Minutes: 150+). Undefined → the kit's default. */
  newChipValue?: (key: string) => CategoricalActive | RangeActive | undefined;
}

// The add-menu's fixed option order (renderers-stats.js:508). Range labels come from the
// active rangeFilters config (relative for Stats, absolute for Rankings/Filter).
const ADD_MENU_KEYS = ["position", "league", "club", "nationality", "foot", "age", "height", "weight"] as const;

// Stats' range popover labels: relative ranges show "−N", the zero label, "+N"; absolute
// ranges just the two domain ends (renderers-stats.js openRangePicker).
function rangeLabels(spec: RangeFilterConfig[string]) {
  const { domain } = spec;
  return spec.relative ? (
    <>
      <span>{domain.min}{spec.unit}</span>
      <span>{spec.zeroLabel}</span>
      <span>+{domain.max}{spec.unit}</span>
    </>
  ) : (
    <>
      <span>{domain.min}{spec.unit}</span>
      <span>{domain.max}{spec.unit}</span>
    </>
  );
}

type OpenPicker = { key: string; pos: PopoverPos };

export function FilterChipBar({
  active,
  refPlayer,
  options,
  rangeFilters,
  onChange,
  addMenuKeys = ADD_MENU_KEYS,
  labels,
  quietKeys,
  newChipValue,
  optionInfo,
}: Props) {
  const labelOf = (key: string) => labels?.[key] ?? FILTER_LABELS[key] ?? key;
  const [menuOpen, setMenuOpen] = useState(false);
  const [valuePicker, openValue, closeValue] = useSlotPicker<OpenPicker>("stats", "value");
  const [rangePicker, openRange, closeRange] = useSlotPicker<OpenPicker>("stats", "range");
  const addWrapRef = useRef<HTMLDivElement>(null);
  // Live only (Ben, 2026-10-16): a value chip is a tick-list — several positions, leagues,
  // clubs or countries ("any of these"). The Season chip stays one season; the demo keeps one value.
  const multi = LIVE && !!valuePicker && valuePicker.key !== "season";

  // Close the add-menu on an outside click (the legacy delegated .add-filter-wrap listener).
  useEffect(() => {
    if (!menuOpen) return;
    const onDown = (e: MouseEvent) => {
      if (!addWrapRef.current?.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [menuOpen]);

  const openPickerAt = (key: string, chipEl: HTMLElement) => {
    const [current, open, close] = isRangeFilter(key, rangeFilters)
      ? [rangePicker, openRange, closeRange]
      : [valuePicker, openValue, closeValue];
    if (current?.key === key) {
      close(); // clicking the open chip again toggles it closed
      return;
    }
    open({ key, pos: popoverPosUnder(chipEl) });
  };

  // Live: a "pick a value" chip added from the menu starts with nothing ticked and opens its list
  // at once — not on the player's own value (Ben, 2026-10-17: "just open up the menu for me").
  const chipValueEls = useRef(new Map<string, HTMLElement>());
  const [openAfterAdd, setOpenAfterAdd] = useState<string | null>(null);
  useEffect(() => {
    if (openAfterAdd === null) return;
    const el = chipValueEls.current.get(openAfterAdd);
    setOpenAfterAdd(null);
    if (el) openValue({ key: openAfterAdd, pos: popoverPosUnder(el) });
  }, [openAfterAdd, openValue]);

  const addFilter = (key: string) => {
    const start = newChipValue?.(key);
    if (LIVE && key !== "season" && !isRangeFilter(key, rangeFilters)) {
      active[key] = [];
      setOpenAfterAdd(key);
    } else if (start !== undefined) {
      active[key] = start;
    } else if (isRangeFilter(key, rangeFilters)) {
      const spec = rangeFilters[key]!;
      // Relative filters open on a small ± sliver around the reference player; an absolute
      // filter opens spanning its whole domain (renderers-stats.js:531).
      active[key] = spec.relative ? { min: -3, max: 3 } : { min: spec.domain.min, max: spec.domain.max };
    } else {
      active[key] = playerFilterDefault(refPlayer, key) ?? "";
    }
    setMenuOpen(false);
    onChange();
  };

  const activeKeys = Object.keys(active).filter((k) => active[k] !== undefined);

  return (
    <>
      {activeKeys.map((key) => {
        // One value with its own list look (a league: flag + name without "(England)") shows it here
        // too (Ben, 2026-10-18). A "Not …" chip or a count keeps plain words.
        const v = active[key] as CategoricalActive;
        const one = Array.isArray(v) ? (v.length === 1 ? v[0]! : null) : isExcludeValue(v) ? null : v;
        const info = !isRangeFilter(key, rangeFilters) && one !== null ? optionInfo?.[key]?.(one) : undefined;
        const label: ReactNode = isRangeFilter(key, rangeFilters)
          ? `${rangeFilters[key]!.chipLabel}: ${formatRangeReadout(key, active[key] as RangeActive, rangeFilters)}`
          : info?.text
            ? (
                <>
                  {labelOf(key)}: {info.icon}
                  {info.text}
                </>
              )
            : `${labelOf(key)}: ${formatCategoricalValue(v)}`;
        return (
          <div className={`filter-chip${quietKeys?.has(key) ? " quiet" : ""}`} key={key}>
            <span
              className="chip-value"
              ref={(el) => {
                if (el) chipValueEls.current.set(key, el);
                else chipValueEls.current.delete(key);
              }}
              onClick={(e) => openPickerAt(key, e.currentTarget)}
            >
              {label}
            </span>
            <span
              className="remove"
              onClick={() => {
                delete active[key];
                // The legacy ✕ closes both chip-bar pickers, wherever they're open.
                closeAllPickers("stats");
                onChange();
              }}
            >
              ✕
            </span>
          </div>
        );
      })}

      <div className="add-filter-wrap" ref={addWrapRef}>
        <button
          className="add-filter-btn"
          onClick={(e) => {
            e.stopPropagation();
            setMenuOpen((o) => !o);
          }}
        >
          + Add filter
        </button>
        <div className={`add-filter-menu${menuOpen ? " open" : ""}`}>
          {addMenuKeys.map((key) => {
            const disabled = active[key] !== undefined;
            const label = isRangeFilter(key, rangeFilters) ? rangeFilters[key]!.label : labelOf(key);
            return (
              <div
                key={key}
                className={`add-filter-option${disabled ? " disabled" : ""}`}
                data-key={key}
                onClick={disabled ? undefined : () => addFilter(key)}
              >
                {label}
                {disabled ? " ✓" : ""}
              </div>
            );
          })}
        </div>
      </div>

      {rangePicker && isRangeFilter(rangePicker.key, rangeFilters) && active[rangePicker.key] !== undefined && (
        <RangePicker
          pos={rangePicker.pos}
          range={active[rangePicker.key] as RangeActive}
          valueToPct={(v) => {
            const { domain } = rangeFilters[rangePicker.key]!;
            const span = domain.max - domain.min;
            return span > 0 ? ((v - domain.min) / span) * 100 : 0;
          }}
          // Whole-number values — Age/Height/Weight are integers (renderers-stats.js:403).
          pxToValue={(px, w) => {
            const { domain } = rangeFilters[rangePicker.key]!;
            return Math.round(domain.min + Math.max(0, Math.min(1, w > 0 ? px / w : 0)) * (domain.max - domain.min));
          }}
          formatReadout={(range) => formatRangeReadout(rangePicker.key, range, rangeFilters)}
          labels={rangeLabels(rangeFilters[rangePicker.key]!)}
          zeroTick={!!rangeFilters[rangePicker.key]!.relative}
          onChange={onChange}
          onClose={closeRange}
        />
      )}
      {valuePicker && !isRangeFilter(valuePicker.key, rangeFilters) && (
        <ValuePicker
          label={labelOf(valuePicker.key)}
          pos={valuePicker.pos}
          options={options[valuePicker.key] ?? []}
          activeValue={categoricalValueOf(active[valuePicker.key] as CategoricalActive | undefined)}
          activeExcluded={isExcludeValue(active[valuePicker.key])}
          selected={multi ? selectedValuesOf(active[valuePicker.key] as CategoricalActive | undefined) : undefined}
          optionInfo={optionInfo?.[valuePicker.key]}
          onPick={(value, exclude) => {
            active[valuePicker.key] = exclude ? excludeValue(value) : value;
            closeValue();
            onChange();
          }}
          // Tick-list (live): the ticks are applied once, when the list closes (Ben, 2026-10-17).
          // None ticked removes the chip, as ✕ does.
          onCommit={
            multi
              ? (values) => {
                  active[valuePicker.key] = categoricalFromValues(values);
                  onChange();
                }
              : undefined
          }
          onClose={closeValue}
        />
      )}
    </>
  );
}
